/**
 * src/services/documents.api.ts 의 짝.
 *
 * 폴더·열·선택지·문서를 만들고 고치고 지우는 것은 로그인한 전원이다 — backend ADR
 * `2026-10-30-let-everyone-own-the-wiki.md`. 지운 폴더와 문서는 휴지통으로 간다.
 *
 * 충돌 규칙도 여기서 흉내 낸다 — 칸(제목·열 값)은 나중에 저장한 쪽이 이기고, 본문(body_md)은
 * 읽은 version 이 지금과 다르면 409 로 거절한다. 긴 글만 덮어쓰면 복구할 수 없기 때문이다.
 */
import { HttpResponse, http } from "msw";
import type {
  DocumentField,
  DocumentFieldInput,
  DocumentFieldPatch,
  DocumentOption,
  DocumentOptionInput,
  DocumentOptionPatch,
  DocumentDetail,
  DocumentInput,
  DocumentPatch,
  DocumentFolderInput,
  DocumentFolder,
  DocumentFolderPatch,
  DocumentTrash,
} from "@/types";
import { db } from "../db";
import { fail, latency, requireAdmin, requireUser } from "../http";

const FOLDERS = "/api/v1/document-folders";
const FIELDS = "/api/v1/document-fields";
const DOCS = "/api/v1/documents";
const w = db.documents;
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;
let nextId = 1000;
const newId = (prefix: string) => `${prefix}${nextId++}`;

const FOLDER_NAME_TAKEN = "같은 이름의 문서 폴더가 있습니다.";
const TITLE_TAKEN = "이 폴더에 같은 이름의 문서가 있습니다.";
/** 서버처럼 폴더 이름과 같은 폴더 안 문서 이름은 겹치지 않는다. 휴지통에 있는 것은 세지 않는다. */
const folderNameTaken = (name: string, exceptId?: string) =>
  w.folders.some((folder) => folder.name === name && folder.id !== exceptId);
const titleTaken = (folderId: string, title: string, exceptId?: string) =>
  w.documents.some(
    (doc) =>
      doc.folder_id === folderId && doc.title === title && !doc.deleted_at && doc.id !== exceptId
  );

/**
 * 문서 폴더 전체의 스냅샷 — 목에서는 처음 물을 때 지난 7일치를 지금 폴더 · 문서로 뜬다. 오래된 버전일수록
 * 지금과 다르게 만든다: 이틀 전부터 폴더마다 첫 문서 본문이 짧고, 사흘 전부터 둘째 · 셋째 문서의 글자 칸
 * 값 하나가 서로 바뀌어 있고, 나흘 전부터는 폴더마다 마지막 문서가 아직 없다(되돌리면 휴지통으로 가는 문서).
 */
type SnapshotDoc = (typeof w.documents)[number];
interface MockSnapshot {
  id: string;
  taken_at: string;
  folders: { folder: DocumentFolder; documents: SnapshotDoc[] }[];
}
/** mock 이 뜰 때 한 번 뜬다 — 처음 열 때 뜨면 그 사이에 만든 폴더 · 문서가 예전 버전에 섞인다. */
const buildSnapshots = (): MockSnapshot[] =>
  Array.from({ length: 7 }, (_, back) => {
    const day = new Date(Date.now() - (back + 1) * 86_400_000);
    day.setHours(21, 0, 0, 0);
    return {
      id: `snap-${back}`,
      taken_at: day.toISOString(),
      folders: w.folders.map((folder) => {
        const documents = structuredClone(
          w.documents.filter((doc) => doc.folder_id === folder.id && !doc.deleted_at)
        );
        if (back >= 1 && documents[0]) documents[0].body_md = documents[0].body_md.split("\n")[0];
        if (back >= 2 && documents[1] && documents[2]) {
          const key = Object.keys(documents[1].values).find(
            (id) =>
              typeof documents[1].values[id] === "string" &&
              typeof documents[2].values[id] === "string" &&
              documents[1].values[id] !== documents[2].values[id]
          );
          if (key)
            [documents[1].values[key], documents[2].values[key]] = [
              documents[2].values[key],
              documents[1].values[key],
            ];
        }
        return {
          folder: structuredClone(folder),
          documents: back >= 3 && documents.length > 1 ? documents.slice(0, -1) : documents,
        };
      }),
    };
  });
const snapshotStore = buildSnapshots();
const allSnapshots = () => snapshotStore;

/** 지금 문서가 스냅샷의 그것과 같은가 — 제목 · 본문 · 값을 본다. */
const sameDocument = (now: (typeof w.documents)[number], saved: (typeof w.documents)[number]) =>
  now.title === saved.title &&
  now.body_md === saved.body_md &&
  JSON.stringify(now.values) === JSON.stringify(saved.values);

/** 지금과 스냅샷이 다른 칸 — 열 id, 제목이 다르면 "title". */
const changedFields = (now: (typeof w.documents)[number], saved: (typeof w.documents)[number]) => [
  ...(now.title !== saved.title ? ["title"] : []),
  ...[...new Set([...Object.keys(now.values), ...Object.keys(saved.values)])].filter(
    (id) => JSON.stringify(now.values[id] ?? null) !== JSON.stringify(saved.values[id] ?? null)
  ),
];

/** 스냅샷 뒤에 그 폴더에 생긴, 지금 있는 문서 — 되돌리면 휴지통으로 간다. */
const addedSince = (folderId: string, documents: SnapshotDoc[]) =>
  w.documents.filter(
    (doc) =>
      doc.folder_id === folderId &&
      !doc.deleted_at &&
      !documents.some((saved) => saved.id === doc.id)
  );

/** 폴더의 지금 상태 — 있는지, 휴지통인지, 없는지. */
const folderPresence = (id: string) =>
  folderOf(id)
    ? "live"
    : trashedFolders.some(({ folder }) => folder.id === id)
      ? "trashed"
      : "gone";

/** 휴지통의 폴더 — 서버처럼 폴더는 목록에서 빠지고 지운 시각과 함께 여기 남는다. */
const trashedFolders: { folder: DocumentFolder; deleted_at: string }[] = [];

/**
 * 휴지통 시드 — 폴더마다 마지막 문서 하나를 하루 · 이틀 전에 따로 지워 둔다(세 폴더까지). 스냅샷을 뜬
 * 뒤에 지운 것이라 이전 버전에서는 「되살아나요」로 보인다.
 */
w.folders.slice(0, 3).forEach((folder, index) => {
  const docs = w.documents.filter((doc) => doc.folder_id === folder.id && !doc.deleted_at);
  const last = docs[docs.length - 1];
  if (last && docs.length > 1)
    last.deleted_at = new Date(Date.now() - (index + 1) * 86_400_000 + 3_600_000).toISOString();
});

/**
 * 휴지통 시드 — 폴더째 지운 것도 하나. 사흘 전에 「구 협력업체」 폴더를 문서 셋과 함께 지웠다. 폴더 목록에는
 * 없고(지운 폴더는 목록에서 빠진다) 휴지통에만 있다. 열은 첫 폴더의 것을 빌린다(id 만 새로).
 */
{
  const template = w.folders[0];
  const stamp = new Date(Date.now() - 3 * 86_400_000).toISOString();
  const fields = (template?.fields ?? []).map((field) => ({
    ...structuredClone(field),
    id: `old-${field.id}`,
  }));
  const folder: DocumentFolder = {
    ...structuredClone(template),
    id: "folder-old-partners",
    slug: "old-partners",
    name: "구 협력업체",
    icon: "archive",
    color: "#8b95a1",
    description: "",
    position: 99,
    fields,
  };
  trashedFolders.push({ folder, deleted_at: stamp });
  const textField = fields.find((field) => field.value_type === "text");
  ["동해구조", "서림설비", "남산조경"].forEach((title, index) => {
    const base = structuredClone(w.documents.find((doc) => doc.folder_id === template.id)!);
    w.documents.push({
      ...base,
      id: `doc-old-partners-${index}`,
      folder_id: folder.id,
      title,
      body_md: "2024년까지 거래한 협력업체. 계약이 끝나 폴더째 정리했다.",
      values: textField
        ? { [textField.id]: index === 0 ? "구조설계" : index === 1 ? "기계설비" : "조경설계" }
        : {},
      position: index,
      deleted_at: stamp,
    });
  });
}

async function guard() {
  await latency();
  const user = requireUser();
  return user instanceof Response ? { denied: user } : { user };
}

/** 본문 앞부분 — 마크다운 기호를 걷고 한 줄로. */
function excerptOf(md: string) {
  return md
    .replace(/^#+\s*/gm, "")
    .replace(/^\s*[-*]\s+(\[[ x]\]\s*)?/gm, "")
    .replace(/[*_`>~]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 140);
}

const summary = ({ body_md: body, version: _version, ...row }: DocumentDetail) => ({
  ...row,
  excerpt: excerptOf(body),
});
const full = (row: DocumentDetail) => ({ ...row, excerpt: excerptOf(row.body_md) });

const folderOf = (id: string) => w.folders.find((folder) => folder.id === id);
const rowOf = (id: string) => w.documents.find((row) => row.id === id);
const fieldOf = (fieldId: string) =>
  w.folders
    .flatMap((folder) => folder.fields.map((field) => ({ folder, field })))
    .find((pair) => pair.field.id === fieldId);

const sortedFolders = (includeInactive: boolean) =>
  w.folders
    .filter((folder) => includeInactive || folder.is_active)
    .sort((a, b) => a.position - b.position)
    .map((folder) => ({
      ...folder,
      fields: [...folder.fields]
        .sort((a, b) => a.position - b.position)
        .map((field) => ({
          ...field,
          options: [...field.options].sort((a, b) => a.position - b.position),
        })),
    }));

/** 선택지를 지우면 그것을 고른 값이 비워진다. */
function dropOptionValues(folderId: string, field: DocumentField, optionId: string) {
  for (const row of w.documents.filter((item) => item.folder_id === folderId)) {
    const value = row.values[field.id];
    if (typeof value === "string" && value === optionId) delete row.values[field.id];
    if (Array.isArray(value)) {
      const left = value.filter((id) => id !== optionId);
      if (left.length) row.values[field.id] = left;
      else delete row.values[field.id];
    }
  }
}

export const documentsHandlers = [
  http.get(`${FOLDERS}/`, async ({ request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const includeInactive = new URL(request.url).searchParams.get("include_inactive") === "true";
    return HttpResponse.json({ results: sortedFolders(includeInactive) });
  }),

  http.post(`${FOLDERS}/`, async ({ request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const { slug, name } = (await request.json()) as DocumentFolderInput;
    if (!name?.trim()) return fail(422, "문서 폴더 이름을 입력하세요.");
    if (!slug || !SLUG.test(slug)) return fail(422, "주소 이름은 영문 소문자와 하이픈이에요.");
    if (w.folders.some((folder) => folder.slug === slug))
      return fail(409, "같은 주소 이름이 있어요.");
    if (folderNameTaken(name.trim())) return fail(409, FOLDER_NAME_TAKEN);
    const folder = {
      id: newId("t-"),
      slug,
      name: name.trim(),
      icon: "",
      color: "",
      description: "",
      position: Math.max(0, ...w.folders.map((item) => item.position)) + 10,
      is_active: true,
      fields: [],
    };
    w.folders.push(folder);
    return HttpResponse.json(folder, { status: 201 });
  }),

  http.patch(`${FOLDERS}/:id/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    if (!folder) return fail(404, "없는 문서 폴더예요.");
    const patch = (await request.json()) as DocumentFolderPatch;
    if (patch.name !== undefined) {
      if (!patch.name.trim()) return fail(422, "문서 폴더 이름을 입력하세요.");
      if (folderNameTaken(patch.name.trim(), folder.id)) return fail(409, FOLDER_NAME_TAKEN);
      folder.name = patch.name.trim();
    }
    if (patch.slug !== undefined) {
      if (!SLUG.test(patch.slug)) return fail(422, "주소 이름은 영문 소문자와 하이픈이에요.");
      if (w.folders.some((item) => item.slug === patch.slug && item.id !== folder.id))
        return fail(409, "같은 주소 이름이 있어요.");
      folder.slug = patch.slug;
    }
    if (patch.icon !== undefined) folder.icon = patch.icon;
    if (patch.color !== undefined) folder.color = patch.color;
    if (patch.description !== undefined) folder.description = patch.description;
    if (patch.position !== undefined) folder.position = patch.position;
    if (patch.is_active !== undefined) folder.is_active = patch.is_active;
    return HttpResponse.json(folder);
  }),

  http.get(`${FOLDERS}/:id/delete-preview/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    if (!folderOf(String(params.id))) return fail(404, "없는 문서 폴더예요.");
    // 서버처럼 휴지통에 없는 문서만 센다 — 함께 휴지통으로 갈 문서 수다.
    const documentCount = w.documents.filter(
      (doc) => doc.folder_id === params.id && !doc.deleted_at
    ).length;
    return HttpResponse.json({ document_count: documentCount });
  }),

  http.delete(`${FOLDERS}/:id/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const id = String(params.id);
    if (!folderOf(id)) return fail(404, "없는 문서 폴더예요.");
    // 서버처럼 폴더는 목록에서 빼고 그 안의 문서는 휴지통으로 보낸다(삭제 시각만 채운다).
    const stamp = new Date().toISOString();
    trashedFolders.push({ folder: folderOf(id)!, deleted_at: stamp });
    w.folders = w.folders.filter((folder) => folder.id !== id);
    for (const doc of w.documents)
      if (doc.folder_id === id && !doc.deleted_at) doc.deleted_at = stamp;
    return new HttpResponse(null, { status: 204 });
  }),

  http.post(`${FOLDERS}/:id/fields/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    if (!folder) return fail(404, "없는 문서 폴더예요.");
    const { name, value_type: valueType } = (await request.json()) as Partial<DocumentFieldInput>;
    if (!name?.trim()) return fail(422, "열 이름을 입력하세요.");
    if (!valueType) return fail(422, "열 종류를 고르세요.");
    const field: DocumentField = {
      id: newId("pr-"),
      name: name.trim(),
      value_type: valueType,
      position: Math.max(0, ...folder.fields.map((item) => item.position)) + 10,
      is_active: true,
      options: [],
    };
    folder.fields.push(field);
    return HttpResponse.json(field, { status: 201 });
  }),

  http.patch(`${FOLDERS}/:id/fields/:fieldId/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    const field = folder?.fields.find((item) => item.id === params.fieldId);
    if (!folder || !field) return fail(404, "없는 열이에요.");
    const patch = (await request.json()) as DocumentFieldPatch & { value_type?: unknown };
    if (patch.value_type !== undefined) return fail(422, "열의 종류는 만든 뒤 바꿀 수 없어요.");
    if (patch.name !== undefined) {
      if (!patch.name.trim()) return fail(422, "열 이름을 입력하세요.");
      field.name = patch.name.trim();
    }
    if (patch.position !== undefined) field.position = patch.position;
    if (patch.is_active !== undefined) field.is_active = patch.is_active;
    return HttpResponse.json(field);
  }),

  http.delete(`${FOLDERS}/:id/fields/:fieldId/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    if (!folder) return fail(404, "없는 문서 폴더예요.");
    const fieldId = String(params.fieldId);
    folder.fields = folder.fields.filter((item) => item.id !== fieldId);
    for (const row of w.documents.filter((item) => item.folder_id === folder.id))
      delete row.values[fieldId];
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 선택지 — 하나씩 더하고 고친다 ───────────────────────────────── */

  http.post(`${FIELDS}/:fieldId/options/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const found = fieldOf(String(params.fieldId));
    if (!found) return fail(404, "없는 열이에요.");
    const { field } = found;
    if (field.value_type !== "select" && field.value_type !== "multi_select")
      return fail(422, "선택지를 둘 수 있는 열이 아니에요.");
    const { name, color = "gray" } = (await request.json()) as DocumentOptionInput;
    if (!name?.trim()) return fail(422, "선택지 이름을 입력하세요.");
    const option: DocumentOption = {
      id: newId("o-"),
      name: name.trim(),
      color,
      position: Math.max(0, ...field.options.map((item) => item.position)) + 10,
    };
    field.options.push(option);
    return HttpResponse.json(option, { status: 201 });
  }),

  http.patch(`${FIELDS}/:fieldId/options/:optionId/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const found = fieldOf(String(params.fieldId));
    const option = found?.field.options.find((item) => item.id === params.optionId);
    if (!option) return fail(404, "없는 선택지예요.");
    const patch = (await request.json()) as DocumentOptionPatch;
    if (patch.name !== undefined) {
      if (!patch.name.trim()) return fail(422, "선택지 이름을 입력하세요.");
      option.name = patch.name.trim();
    }
    if (patch.color !== undefined) option.color = patch.color;
    if (patch.position !== undefined) option.position = patch.position;
    return HttpResponse.json(option);
  }),

  http.delete(`${FIELDS}/:fieldId/options/:optionId/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const found = fieldOf(String(params.fieldId));
    if (!found) return fail(404, "없는 열이에요.");
    const optionId = String(params.optionId);
    found.field.options = found.field.options.filter((item) => item.id !== optionId);
    dropOptionValues(found.folder.id, found.field, optionId);
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 문서 ────────────────────────────────────────────────────────── */

  http.get(`${FOLDERS}/:id/documents/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    if (!folderOf(String(params.id))) return fail(404, "없는 문서 폴더예요.");
    const query = new URL(request.url).searchParams;
    const projectId = query.get("project_id");
    const from = query.get("period_from");
    const to = query.get("period_to");
    const keyword = (query.get("keyword") ?? "").trim().toLowerCase();
    const results = w.documents
      .filter((row) => row.folder_id === params.id && !row.deleted_at)
      .filter((row) => !projectId || row.project_id === projectId)
      .filter((row) => !from || row.created_at.slice(0, 10) >= from)
      .filter((row) => !to || row.created_at.slice(0, 10) <= to)
      .filter(
        (row) =>
          !keyword ||
          row.title.toLowerCase().includes(keyword) ||
          row.body_md.toLowerCase().includes(keyword)
      )
      .sort((a, b) => a.position - b.position)
      .map(summary);
    return HttpResponse.json({ results });
  }),

  // 스냅샷은 관리자만 — 문서 폴더 전체를 하루 한 번(21시) 뜬다. 목에서는 처음 열 때 지난 7일치를 뜬다.
  http.get("/api/v1/admin/document-snapshots/", async () => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    return HttpResponse.json({
      results: allSnapshots().map(({ id, taken_at, folders }) => ({
        id,
        taken_at,
        folder_count: folders.length,
        document_count: folders.reduce((sum, item) => sum + item.documents.length, 0),
      })),
    });
  }),

  http.get("/api/v1/admin/document-snapshots/:id/", async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const found = allSnapshots().find((item) => item.id === params.id);
    if (!found) return fail(404, "버전을 찾을 수 없습니다.");
    const saved = new Set(found.folders.map(({ folder }) => folder.id));
    return HttpResponse.json({
      id: found.id,
      taken_at: found.taken_at,
      folder_count: found.folders.length,
      document_count: found.folders.reduce((sum, item) => sum + item.documents.length, 0),
      folders: found.folders.map(({ folder, documents }) => ({
        folder,
        current: folderPresence(folder.id),
        documents: documents.map((doc) => {
          const now = w.documents.find((row) => row.id === doc.id);
          const live = !!now && !now.deleted_at;
          return {
            id: doc.id,
            title: doc.title,
            excerpt: excerptOf(doc.body_md),
            updated_at: doc.updated_at,
            current: !now ? "gone" : now.deleted_at ? "trashed" : "live",
            changed: live && !sameDocument(now, doc),
            values: doc.values,
            changed_fields: live ? changedFields(now, doc) : [],
            body_changed: live && now.body_md !== doc.body_md,
          };
        }),
        added: addedSince(folder.id, documents).map((doc) => ({
          id: doc.id,
          title: doc.title,
          values: doc.values,
        })),
      })),
      added_folders: w.folders
        .filter((folder) => !saved.has(folder.id))
        .map((folder) => ({
          id: folder.id,
          name: folder.name,
          icon: folder.icon,
          color: folder.color,
          document_count: w.documents.filter(
            (doc) => doc.folder_id === folder.id && !doc.deleted_at
          ).length,
        })),
    });
  }),

  // 그 시각의 문서 하나 — 표의 한 줄 + 본문, 지금 문서가 있으면 지금 제목 · 본문.
  http.get("/api/v1/admin/document-snapshots/:id/documents/:documentId/", async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const found = allSnapshots().find((item) => item.id === params.id);
    if (!found) return fail(404, "버전을 찾을 수 없습니다.");
    const doc = found.folders
      .flatMap((item) => item.documents)
      .find((d) => d.id === params.documentId);
    if (!doc) return fail(404, "이 버전에 없는 문서입니다.");
    const now = w.documents.find((row) => row.id === doc.id);
    const live = !!now && !now.deleted_at;
    return HttpResponse.json({
      id: doc.id,
      title: doc.title,
      excerpt: excerptOf(doc.body_md),
      updated_at: doc.updated_at,
      current: !now ? "gone" : now.deleted_at ? "trashed" : "live",
      changed: live && !sameDocument(now, doc),
      values: doc.values,
      changed_fields: live ? changedFields(now, doc) : [],
      body_changed: live && now.body_md !== doc.body_md,
      body_md: doc.body_md,
      now: live ? { title: now.title, body_md: now.body_md, values: now.values } : null,
    });
  }),

  // 전체를 되돌린다 — 그때 있던 폴더 · 문서는 그 내용으로(휴지통 · 없음이면 되살려), 그 뒤에 만든 폴더 ·
  // 문서는 휴지통으로. 폴더 하나, 문서 하나씩 되돌리지 않는다.
  http.post("/api/v1/admin/document-snapshots/:id/restore/", async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const found = allSnapshots().find((item) => item.id === params.id);
    if (!found) return fail(404, "버전을 찾을 수 없습니다.");
    const stamp = new Date().toISOString();
    const saved = new Set(found.folders.map(({ folder }) => folder.id));
    let trashed = 0;
    let trashedFolderCount = 0;
    // 그 뒤에 만든 폴더 — 폴더 지우기와 같이 휴지통으로(안의 문서도 같은 시각으로).
    for (const folder of w.folders.filter((item) => !saved.has(item.id))) {
      trashedFolders.push({ folder, deleted_at: stamp });
      for (const doc of w.documents)
        if (doc.folder_id === folder.id && !doc.deleted_at) {
          doc.deleted_at = stamp;
          trashed++;
        }
      trashedFolderCount++;
    }
    w.folders = w.folders.filter((item) => saved.has(item.id));
    let revivedFolders = 0;
    let reverted = 0;
    let revived = 0;
    for (const { folder, documents } of found.folders) {
      if (!folderOf(folder.id)) {
        const index = trashedFolders.findIndex((item) => item.folder.id === folder.id);
        if (index >= 0) trashedFolders.splice(index, 1);
        w.folders.push(structuredClone(folder));
        revivedFolders++;
      } else {
        // 열 · 이름 · 아이콘은 그 시각으로 — 칸 값이 그 열을 가리킨다.
        Object.assign(folderOf(folder.id)!, structuredClone(folder));
      }
      for (const doc of addedSince(folder.id, documents)) {
        doc.deleted_at = stamp;
        trashed++;
      }
      for (const savedDoc of documents) {
        const index = w.documents.findIndex((row) => row.id === savedDoc.id);
        const now = index < 0 ? null : w.documents[index];
        if (now && !now.deleted_at && sameDocument(now, savedDoc)) continue;
        if (!now || now.deleted_at) revived++;
        else reverted++;
        const restored = { ...structuredClone(savedDoc), deleted_at: null, updated_at: stamp };
        if (index < 0) w.documents.push(restored);
        else w.documents[index] = { ...restored, version: w.documents[index].version + 1 };
      }
    }
    w.folders.sort((a, b) => a.position - b.position);
    return HttpResponse.json({
      reverted,
      revived,
      trashed,
      revived_folders: revivedFolders,
      trashed_folders: trashedFolderCount,
      skipped_fields: [],
    });
  }),

  // 휴지통은 관리자만 — 지운 문서는 폴더째 지운 것까지 한 목록으로(folder_trashed).
  http.get("/api/v1/admin/document-trash/", async () => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const latest = <T extends { deleted_at: string | null }>(a: T, b: T) =>
      a.deleted_at! < b.deleted_at! ? 1 : -1;
    const trash: DocumentTrash = {
      folders: [...trashedFolders].sort(latest).map(({ folder, deleted_at }) => ({
        id: folder.id,
        name: folder.name,
        icon: folder.icon,
        color: folder.color,
        deleted_at,
        document_count: w.documents.filter(
          (doc) => doc.folder_id === folder.id && doc.deleted_at === deleted_at
        ).length,
      })),
      // 지운 문서 전부 — 폴더째 지운 것도 넣고 folder_trashed 로 알린다.
      documents: w.documents
        .filter((row) => row.deleted_at && folderPresence(row.folder_id) !== "gone")
        .sort(latest)
        .map((row) => {
          const live = folderOf(row.folder_id);
          const trashed = trashedFolders.find(({ folder }) => folder.id === row.folder_id);
          return {
            ...summary(row),
            folder_name: (live ?? trashed!.folder).name,
            folder_trashed: !live,
          };
        }),
    };
    return HttpResponse.json(trash);
  }),

  // 휴지통의 문서 하나 — 미리 보기. 원래 폴더가 지워졌어도 그 폴더(열)를 같이 준다.
  http.get("/api/v1/admin/document-trash/documents/:id/", async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const row = w.documents.find((doc) => doc.id === params.id && doc.deleted_at);
    if (!row) return fail(404, "휴지통에 없는 문서입니다.");
    const folder =
      folderOf(row.folder_id) ??
      trashedFolders.find(({ folder }) => folder.id === row.folder_id)?.folder;
    if (!folder) return fail(404, "원래 문서 폴더가 없습니다.");
    return HttpResponse.json({ document: full(row), folder });
  }),

  http.get("/api/v1/admin/document-trash/folders/:id/documents/", async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const trashed = trashedFolders.find(({ folder }) => folder.id === params.id);
    if (!trashed) return fail(409, "휴지통에 있는 문서 폴더만 되살릴 수 있습니다.");
    const results = w.documents
      .filter((doc) => doc.folder_id === trashed.folder.id && doc.deleted_at === trashed.deleted_at)
      .sort((a, b) => a.position - b.position)
      .map(summary);
    return HttpResponse.json({ results });
  }),

  http.post("/api/v1/admin/document-folders/:id/restore/", async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const index = trashedFolders.findIndex(({ folder }) => folder.id === params.id);
    if (index < 0) return fail(409, "휴지통에 있는 문서 폴더만 되살릴 수 있습니다.");
    const { folder, deleted_at } = trashedFolders[index];
    if (folderNameTaken(folder.name)) return fail(409, FOLDER_NAME_TAKEN);
    trashedFolders.splice(index, 1);
    w.folders.push(folder);
    w.folders.sort((a, b) => a.position - b.position);
    for (const doc of w.documents)
      if (doc.folder_id === folder.id && doc.deleted_at === deleted_at) doc.deleted_at = null;
    return HttpResponse.json(folder);
  }),

  http.post(`${FOLDERS}/:id/documents/`, async ({ params, request }) => {
    const { denied, user } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    if (!folder) return fail(404, "없는 문서 폴더예요.");
    const input = (await request.json()) as DocumentInput;
    if (titleTaken(folder.id, input.title?.trim() ?? "")) return fail(409, TITLE_TAKEN);
    const stamp = new Date().toISOString();
    const row: DocumentDetail = {
      id: newId("r-"),
      folder_id: folder.id,
      title: input.title?.trim() ?? "",
      excerpt: "",
      project_id: input.project_id ?? null,
      position: Math.max(0, ...w.documents.map((item) => item.position)) + 10,
      values: input.values ?? {},
      body_md: input.body_md ?? "",
      version: 1,
      created_by: user.id,
      created_at: stamp,
      updated_by: user.id,
      updated_at: stamp,
      deleted_at: null,
    };
    w.documents.push(row);
    return HttpResponse.json(full(row), { status: 201 });
  }),

  http.get(`${DOCS}/:id/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const row = rowOf(String(params.id));
    return row ? HttpResponse.json(full(row)) : fail(404, "없는 문서예요.");
  }),

  http.patch(`${DOCS}/:id/`, async ({ params, request }) => {
    const { denied, user } = await guard();
    if (denied) return denied;
    const row = rowOf(String(params.id));
    if (!row) return fail(404, "없는 문서예요.");
    const patch = (await request.json()) as DocumentPatch;
    if (patch.title !== undefined && titleTaken(row.folder_id, patch.title.trim(), row.id))
      return fail(409, TITLE_TAKEN);
    if (patch.body_md !== undefined) {
      if (patch.version !== row.version)
        return fail(409, "다른 사람이 먼저 본문을 고쳤어요. 최신 내용을 불러올게요.");
      row.body_md = patch.body_md;
      row.version += 1;
    }
    if (patch.title !== undefined) row.title = patch.title.trim();
    if (patch.project_id !== undefined) row.project_id = patch.project_id;
    for (const [key, value] of Object.entries(patch.values ?? {})) {
      if (value === null || (Array.isArray(value) && value.length === 0)) delete row.values[key];
      else row.values[key] = value;
    }
    row.updated_at = new Date().toISOString();
    row.updated_by = user.id;
    return HttpResponse.json(full(row));
  }),

  http.delete(`${DOCS}/:id/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const row = rowOf(String(params.id));
    if (!row) return fail(404, "없는 문서예요.");
    // 휴지통으로 — 행은 지우지 않고 삭제 시각만 채운다.
    row.deleted_at = new Date().toISOString();
    return new HttpResponse(null, { status: 204 });
  }),

  http.post("/api/v1/admin/documents/:id/restore/", async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const row = w.documents.find((doc) => doc.id === params.id);
    if (!row) return fail(404, "없는 문서예요.");
    // 폴더도 휴지통에 있으면 폴더를 먼저 되살려 자리를 만든다 — 그 폴더와 같이 지운 다른 문서는 휴지통에 남는다.
    if (!folderOf(row.folder_id)) {
      const index = trashedFolders.findIndex(({ folder }) => folder.id === row.folder_id);
      if (index < 0) return fail(409, "원래 문서 폴더가 없어 복원할 수 없습니다.");
      const { folder } = trashedFolders[index];
      if (folderNameTaken(folder.name)) return fail(409, FOLDER_NAME_TAKEN);
      trashedFolders.splice(index, 1);
      w.folders.push(folder);
      w.folders.sort((a, b) => a.position - b.position);
    }
    if (titleTaken(row.folder_id, row.title, row.id)) return fail(409, TITLE_TAKEN);
    row.deleted_at = null;
    return HttpResponse.json(full(row));
  }),
];
